import { lazy, Suspense } from 'react';
import { Skeleton } from '@mui/material';
import useGetCountryLandingData from '../../../hooks/useGetCountryLandingData';
import useGetMainRouteLandingData from '../../../hooks/useGetMainRouteLandingData';
import {
  getCountryEditorial,
  getRouteEditorial,
  nameToSlug,
} from '../../../components/LandingPageTemplate';
import type {
  LandingBoatCard,
  LandingRouteCard,
} from '../../../components/LandingPageTemplate';
import { formatBoatName } from '../../../common/utilities/formatBoatName';
import { buildCountryPath, buildMainRoutePath } from '../../../seo/metadata';

const LandingPageTemplate = lazy(
  () => import('../../../components/LandingPageTemplate/LandingPageTemplate'),
);

type CountryLandingSectionProps = {
  countrySlug: string;
  countryId: number;
  entityName: string;
};

type RouteLandingSectionProps = {
  mainRouteSlug: string;
  mainRouteId: number;
  entityName: string;
};

function mapBoats(
  boats: {
    boatId: number;
    name?: string | null;
    slug?: string | null;
    currency?: string | null;
    minPrice?: number | null;
    heroImageUrl?: string | null;
  }[],
): LandingBoatCard[] {
  return boats.map(boat => ({
    boatId: boat.boatId,
    name: boat.name?.trim() || `Boat ${boat.boatId}`,
    slug:
      boat.slug?.trim() || formatBoatName(boat.name ?? `boat-${boat.boatId}`),
    currency: boat.currency,
    minPrice: boat.minPrice,
    heroImageUrl: boat.heroImageUrl,
  }));
}

export function CountryLandingSection({
  countrySlug,
  countryId,
  entityName,
}: CountryLandingSectionProps) {
  const { countryLandingData, countryLandingLoading } =
    useGetCountryLandingData({
      countryId: countryId > 0 ? countryId : undefined,
      slug: countrySlug,
    });

  if (countryLandingLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={320}
        animation="wave"
      />
    );
  }

  const data = countryLandingData;
  const displayName = data?.countryName?.trim() || entityName;
  const editorial = getCountryEditorial(countrySlug, displayName);

  const routes: LandingRouteCard[] =
    data?.routes.map(route => ({
      routeId: route.routeId,
      routeName: route.routeName?.trim() || `Route ${route.routeId}`,
      routeImageUrl: route.routeImageUrl,
      isTopDestination: route.isTopDestination,
      departureCount: route.departureCount,
      slug: nameToSlug(route.routeName ?? `route-${route.routeId}`),
    })) ?? [];

  const boats = mapBoats(data?.boats ?? []);

  return (
    <Suspense
      fallback={
        <Skeleton
          variant="rectangular"
          width="100%"
          height={320}
          animation="wave"
        />
      }
    >
      <LandingPageTemplate
        variant="country"
        entityName={displayName}
        heroImageUrl={routes[0]?.routeImageUrl}
        editorial={editorial}
        minPrice={data?.minPrice}
        maxPrice={data?.maxPrice}
        currency={data?.currency}
        departureCount={data?.departureCount ?? 0}
        nextDepartureDate={data?.nextDepartureDate}
        boats={boats}
        routes={routes}
        breadcrumbs={[
          { name: 'Home', href: '/' },
          {
            name: displayName,
            href: buildCountryPath(countrySlug),
          },
        ]}
      />
    </Suspense>
  );
}

export function RouteLandingSection({
  mainRouteSlug,
  mainRouteId,
  entityName,
}: RouteLandingSectionProps) {
  const { mainRouteLandingData, mainRouteLandingLoading } =
    useGetMainRouteLandingData({
      mainRouteId: mainRouteId > 0 ? mainRouteId : undefined,
      slug: mainRouteSlug,
    });

  if (mainRouteLandingLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={320}
        animation="wave"
      />
    );
  }

  const data = mainRouteLandingData;
  const displayName = entityName || data?.mainRouteName?.trim() || 'Route';
  const countryName = data?.countryName?.trim() || null;
  const countrySlug = countryName ? nameToSlug(countryName) : null;
  const editorial = getRouteEditorial(mainRouteSlug, displayName, countryName);
  const boats = mapBoats(data?.boats ?? []);

  return (
    <Suspense
      fallback={
        <Skeleton
          variant="rectangular"
          width="100%"
          height={320}
          animation="wave"
        />
      }
    >
      <LandingPageTemplate
        variant="route"
        entityName={displayName}
        heroImageUrl={data?.routeImageUrl}
        editorial={editorial}
        minPrice={data?.minPrice}
        maxPrice={data?.maxPrice}
        currency={data?.currency}
        departureCount={data?.departureCount ?? 0}
        nextDepartureDate={data?.nextDepartureDate}
        boats={boats}
        countryName={countryName}
        countrySlug={countrySlug}
        breadcrumbs={[
          { name: 'Home', href: '/' },
          ...(countryName && countrySlug
            ? [
                {
                  name: countryName,
                  href: buildCountryPath(countrySlug),
                },
              ]
            : []),
          {
            name: displayName,
            href: buildMainRoutePath(mainRouteSlug),
          },
        ]}
      />
    </Suspense>
  );
}
